import React, { useRef, useState } from "react";
import {
  Col,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { TbDownload, TbUpload } from "react-icons/tb";
import { toast } from "react-toastify";
import moment from "moment";

import { fetchWrapper } from "@helpers/fetchwrapper";
import SelectAsyncPaginate from "@areas/shared/components/select/SelectAsyncPaginate";

import styles from "@areas/shared/components/personal/modals/styles/customModal.module.css";

const TEMPLATE_FILENAME = "shablon_meropriyatiy.csv";

const loadCompaniesOptions = async (searchQuery, loadedOptions, { page }) => {
  const url = `/v1/api/app/findCompanies/?text=${searchQuery}&offset=${
    page * 10
  }&limit=10`;

  const result = await fetchWrapper.post(url, [1, 2, 3, 4]);

  const options = result.data.map((p) => ({
    value: p.Id,
    label: p.Shortname ? p.Shortname : p.Fullname,
  }));

  return {
    options,
    hasMore: result.total > page * 10,
    additional: {
      page: searchQuery ? 1 : page + 1,
    },
  };
};

const downloadTemplate = () => {
  const header =
    "Наименование;Описание;Тип;Дата начала;Дата окончания;Теги";
  const hint =
    "# Тип: 1 — Общественное, 2 — День открытых дверей, 3 — День без турникета. Даты: ДД.ММ.ГГГГ. Теги через запятую.";
  const example =
    "День открытых дверей;Экскурсия по предприятию;2;01.09.2026;01.09.2026;школьники,карьера";
  const content = `\uFEFF${header}\n${hint}\n${example}\n`;
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = TEMPLATE_FILENAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const parseDate = (value) => {
  if (!value) return null;
  const formats = ["DD.MM.YYYY", "DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY"];
  for (const format of formats) {
    const parsed = moment(value.trim(), format, true);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DD");
    }
  }
  const loose = moment(value.trim());
  return loose.isValid() ? loose.format("YYYY-MM-DD") : null;
};

const splitCsvLine = (line, delimiter) => {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

const parseCsvRows = (text) => {
  const normalized = text.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalized.split("\n").filter((line) => line.trim() && !line.trim().startsWith("#"));
  if (lines.length < 2) {
    return [];
  }

  const delimiter = lines[0].includes(";") ? ";" : ",";
  const headers = splitCsvLine(lines[0], delimiter).map((h) =>
    h.toLowerCase().replace(/\s+/g, "")
  );

  const indexOf = (...names) =>
    headers.findIndex((h) => names.some((name) => h.includes(name)));

  const nameIdx = indexOf("наименование", "name");
  const descriptionIdx = indexOf("описание", "description");
  const typeIdx = indexOf("тип", "idtype", "type");
  const dateStartIdx = indexOf("датаначала", "datestart", "начало");
  const dateEndIdx = indexOf("датаокончания", "dateend", "конец", "окончание");
  const tagsIdx = indexOf("теги", "tags");

  if (nameIdx < 0) {
    throw new Error("В шаблоне не найдена колонка «Наименование»");
  }

  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cols = splitCsvLine(lines[i], delimiter);
    const name = cols[nameIdx] || "";
    if (!name) continue;

    rows.push({
      name,
      description: descriptionIdx >= 0 ? cols[descriptionIdx] || "" : "",
      type: typeIdx >= 0 ? parseInt(cols[typeIdx], 10) || 1 : 1,
      dateStart: dateStartIdx >= 0 ? cols[dateStartIdx] : "",
      dateEnd: dateEndIdx >= 0 ? cols[dateEndIdx] : "",
      tags: tagsIdx >= 0 ? cols[tagsIdx] || "" : "",
      line: i + 1,
    });
  }

  return rows;
};

const insertEventRow = (row, companyId) => {
  const dateStart = parseDate(row.dateStart) || moment().format("YYYY-MM-DD");
  const dateEnd = parseDate(row.dateEnd) || dateStart;

  const data = new FormData();
  data.append("idType", row.type >= 1 && row.type <= 3 ? row.type : 1);
  data.append("name", row.name);
  data.append("description", row.description);
  data.append("idCompany", companyId);
  data.append("dateStart", dateStart);
  data.append("dateEnd", dateEnd);

  if (row.tags) {
    row.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean)
      .forEach((tag) => data.append("tags[]", tag));
  }

  return fetchWrapper.post(
    `/v1/api/admin/insertevent?idAddress=1`,
    data,
    false
  );
};

function ModalImport({ isOpen, toggle, onSuccess }) {
  const fileInputRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCompany(null);
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    toggle();
  };

  const handleUpload = async () => {
    if (!company?.value) {
      toast.warning("Выберите компанию");
      return;
    }
    if (!file) {
      toast.warning("Выберите файл для загрузки");
      return;
    }

    const isCsv = /\.csv$/i.test(file.name) || file.type.includes("csv");
    setLoading(true);

    try {
      if (isCsv) {
        const text = await file.text();
        const rows = parseCsvRows(text);
        if (!rows.length) {
          throw new Error("В файле нет строк с мероприятиями");
        }

        let successCount = 0;
        const errors = [];

        for (const row of rows) {
          try {
            await insertEventRow(row, company.value);
            successCount += 1;
          } catch (error) {
            errors.push(`Строка ${row.line}: ${error}`);
          }
        }

        toast.success(
          `Загружено мероприятий: ${successCount} из ${rows.length} для «${company.label}»`
        );
        errors.slice(0, 5).forEach((error) => toast.warning(error));
        if (errors.length > 5) {
          toast.warning(`...и ещё ${errors.length - 5} ошибок`);
        }

        if (successCount > 0) {
          onSuccess?.();
          reset();
          toggle();
        }
      } else {
        const data = new FormData();
        data.append("file", file);

        const result = await fetchWrapper.post(
          `/v1/api/admin/ImportEvents?idCompany=${company.value}`,
          data,
          false
        );

        const processed = result.processed ?? result.Processed ?? 0;
        const totalRows = result.totalRows ?? result.TotalRows ?? processed;
        const errors = result.errors ?? result.Errors ?? [];

        toast.success(
          `Обработано: ${processed} из ${totalRows} для «${company.label}»`
        );
        errors.slice(0, 5).forEach((error) => toast.warning(error));
        if (errors.length > 5) {
          toast.warning(`...и ещё ${errors.length - 5} ошибок`);
        }

        onSuccess?.();
        reset();
        toggle();
      }
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : error?.message || "Не удалось загрузить мероприятия"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      size="lg"
      className={styles.modal}
      modalClassName="zoomIn"
    >
      <ModalHeader toggle={handleClose} className={styles.header}>
        <div className={styles.title}>Загрузка мероприятий из Excel</div>
      </ModalHeader>
      <ModalBody className={styles.body}>
        <Row className="g-3">
          <Col sm={12}>
            <Label className={styles.label}>Компания</Label>
            <SelectAsyncPaginate
              loadFunc={loadCompaniesOptions}
              value={company}
              placeholder="Найдите и выберите компанию"
              onChangeValue={setCompany}
            />
          </Col>
          <Col sm={12}>
            <Label className={styles.label}>Файл (.csv, .xlsx)</Label>
            <InputFile
              fileInputRef={fileInputRef}
              file={file}
              onChange={setFile}
            />
          </Col>
          <Col sm={12}>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={downloadTemplate}
            >
              <TbDownload className="me-1" />
              Скачать шаблон для загрузки
            </button>
            <div className="text-muted mt-2" style={{ fontSize: "0.8rem" }}>
              Шаблон открывается в Excel. Заполните строки и загрузите файл,
              выбрав компанию, для которой создаются мероприятия.
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter className={styles.footer}>
        <div className="hstack gap-2 justify-content-end">
          <button
            type="button"
            className={`btn btn-light ${styles.close}`}
            onClick={handleClose}
            disabled={loading}
          >
            Закрыть
          </button>
          <button
            type="button"
            className={`btn btn-success ${styles.submit}`}
            onClick={handleUpload}
            disabled={loading}
          >
            <TbUpload className="me-1" />
            {loading ? "Загрузка..." : "Загрузить"}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

function InputFile({ fileInputRef, file, onChange }) {
  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className={`form-control ${styles.input}`}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      {file ? (
        <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
          Выбран файл: {file.name}
        </div>
      ) : null}
    </div>
  );
}

export default ModalImport;
